import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { Eye, Loader2, Lock, ExternalLink as LinkIcon } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function PortalAccessDialog({ open, onOpenChange, clientId, clientName }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!open || !clientId) return;
        setLoading(true);
        api.get(`/clients/${clientId}/portal-access`)
            .then(({ data }) => setData(data))
            .catch(() => setData({ total_opens: 0, recent: [] }))
            .finally(() => setLoading(false));
    }, [open, clientId]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] rounded-xl border-slate-200">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight inline-flex items-center gap-2">
                        <Eye className="w-4 h-4 text-[#C55A46]" /> Portal access
                    </DialogTitle>
                    <DialogDescription className="text-slate-500">
                        When <span className="text-slate-700 font-medium">{clientName}</span> has opened the portal.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-lg border border-slate-200 p-3">
                                <p className="text-xs text-slate-500">Total opens</p>
                                <p className="font-display text-2xl tracking-tight text-slate-900 mt-1" data-testid="access-total">
                                    {data?.total_opens ?? 0}
                                </p>
                            </div>
                            <div className="rounded-lg border border-slate-200 p-3">
                                <p className="text-xs text-slate-500">Last access</p>
                                <p className="text-sm text-slate-900 mt-1.5" data-testid="access-last">
                                    {data?.last_access ? formatSafe(data.last_access) : "Never"}
                                </p>
                            </div>
                        </div>

                        <p className="overline mt-4 mb-2">Recent history</p>
                        {(!data?.recent || data.recent.length === 0) ? (
                            <p className="text-sm text-slate-500 text-center py-6">No portal opens yet.</p>
                        ) : (
                            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden max-h-80 overflow-y-auto"
                                data-testid="access-list">
                                {data.recent.map((r) => (
                                    <li key={r.access_id} data-testid={`access-row-${r.access_id}`}
                                        className="flex items-center gap-3 px-4 py-3 bg-white">
                                        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
                                            r.event_type === "portal_unlocked" ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"
                                        }`}>
                                            {r.event_type === "portal_unlocked" ? <Lock className="w-3.5 h-3.5" /> : <LinkIcon className="w-3.5 h-3.5" />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm text-slate-900">
                                                {r.event_type === "portal_unlocked" ? "Client unlocked the protected portal" : "Client opened the portal"}
                                            </p>
                                            <p className="text-xs text-slate-400 mt-0.5">{formatSafe(r.created_at)}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

function formatSafe(iso) {
    if (!iso) return "";
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
    catch { return iso; }
}
